import React, { useCallback, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Download, ExternalLink, Image, Link, Mail, Share2 } from "lucide-react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { Button, Dialog } from "@/components/ui";

interface VideoPageShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
  videoPageUrl: string;
  thumbnailUrl: string;
  videoPageTitle: string;
  isHorizontalVideo: boolean;
  markAsShared: () => void;
}

export const VideoPageShareDialog: React.FC<VideoPageShareDialogProps> = ({
  open,
  onOpenChange,
  onDone,
  videoPageUrl,
  thumbnailUrl,
  videoPageTitle,
  isHorizontalVideo,
  markAsShared,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedForEmail, setCopiedForEmail] = useState(false);

  const handleCopyLink = useCallback(() => {
    if (!videoPageUrl) return;

    markAsShared();
    const htmlContent = `<a href="${videoPageUrl}" target="_blank" style="color:#0066cc; text-decoration:underline; font-family:inherit;">${videoPageUrl}</a>`;

    const handleSuccess = () => {
      setCopiedLink(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => {
        setCopiedLink(false);
      }, 2000);
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const item = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([videoPageUrl], { type: "text/plain" }),
      });
      navigator.clipboard
        .write([item])
        .then(handleSuccess)
        .catch(err => {
          console.error("Could not copy text: ", err);
          copy(videoPageUrl);
          handleSuccess();
        });
    } else {
      copy(videoPageUrl);
      handleSuccess();
    }
  }, [videoPageUrl, markAsShared]);

  const handleCopyForEmail = useCallback(() => {
    if (!videoPageUrl) return;

    markAsShared();
    const width = isHorizontalVideo ? 600 : 250;
    const widthPx = `${width}px`;

    const htmlContent = `
        <br/>
        <a href="${videoPageUrl}" target="_blank" style="width:100%; text-decoration:none; font-family:inherit; font-weight:normal; padding:0; text-align:left; display:inline-block;">
          <img src="${thumbnailUrl}" alt="${
      videoPageTitle || "Watch my quick video"
    }" width="${width}" style="width:${widthPx}; height:auto; border:none; outline:none; text-decoration:none; display:block;" />
        </a>
        <br/>
        <a href="${videoPageUrl}" target="_blank" style="color:#0066cc; text-decoration:underline; font-family:inherit;">Click here to watch my video!</a>
        <br>
    `;

    const handleSuccess = () => {
      setCopiedForEmail(true);
      toast.success("Playable video thumbnail copied! Paste in your email.");
      setTimeout(() => {
        setCopiedForEmail(false);
      }, 2000);
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const item = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([videoPageUrl], { type: "text/plain" }),
      });
      navigator.clipboard
        .write([item])
        .then(handleSuccess)
        .catch(err => {
          console.error("Could not copy text: ", err);
          copy(htmlContent, { format: "text/html" });
          handleSuccess();
        });
    } else {
      copy(htmlContent, { format: "text/html" });
      handleSuccess();
    }
  }, [videoPageUrl, videoPageTitle, isHorizontalVideo, thumbnailUrl, markAsShared]);

  const downloadQrCode = () => {
    const svg = document.getElementById("share-dialog-qr-code");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    canvas.width = 1000;
    canvas.height = 1000;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const svgElement = svg.cloneNode(true) as SVGElement;
    svgElement.setAttribute("width", "1000");
    svgElement.setAttribute("height", "1000");

    const svgString = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 1000, 1000);
      URL.revokeObjectURL(svgUrl);

      try {
        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = "video-page-qr-code.png";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        toast.success("QR Code downloaded successfully!");
      } catch (err) {
        console.error("Failed to generate PNG URL", err);
        toast.error("Failed to export as PNG.");
      }
    };
    img.src = svgUrl;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      showCloseButton={true}
      className="max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden"
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-slate-800 text-lg font-bold">
          <Share2 className="size-5 text-indigo-600" />
          <span>Share Video Page</span>
        </Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="p-5 bg-white flex flex-col gap-5 items-stretch select-none max-h-[calc(100vh-12rem)] overflow-y-auto">
        {/* Top URL Input and Open Button */}
        <div className="flex gap-2 w-full">
          <input
            type="text"
            readOnly
            value={videoPageUrl}
            className="flex-1 bg-slate-50 border border-slate-200 text-slate-500 font-mono text-xs select-all h-9 py-2 px-3 rounded-lg focus:outline-none focus:ring-0 focus:border-slate-200 cursor-default"
          />
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-9 px-3 gap-1.5 text-xs font-semibold"
            onClick={() => window.open(videoPageUrl, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="size-3.5" />
            <span>Open</span>
          </Button>
        </div>

        {/* Bottom Split Layout */}
        <div className="flex flex-row gap-5 items-stretch">
          {/* Left Column (QR Code Card) */}
          <div className="w-40 flex flex-col items-center justify-between gap-3 bg-slate-50 p-4 border border-slate-100 rounded-xl">
            <div className="text-center">
              <span className="text-xs font-semibold text-slate-500">Scan QR Code</span>
            </div>
            <div className="bg-white p-2 rounded-lg border border-slate-200/50 shadow-inner flex items-center justify-center shrink-0 select-none animate-in fade-in zoom-in-95 duration-200">
              <QRCodeSVG id="share-dialog-qr-code" value={videoPageUrl} size={110} level="L" marginSize={1} />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs gap-1.5 h-8 font-semibold shadow-2xs hover:shadow-xs transition-all active:scale-[0.98]"
              onClick={downloadQrCode}
            >
              <Download className="size-3.5 shrink-0 text-slate-500" />
              <span>Download</span>
            </Button>
          </div>

          {/* Right Column (Action Tiles) */}
          <div className="flex-1 flex flex-col gap-3">
            {/* Tile 1: Copy Page Link */}
            <div
              onClick={handleCopyLink}
              className={`group flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 active:scale-[0.98] cursor-pointer select-none ${
                copiedLink
                  ? "border-emerald-500 bg-emerald-50/10 shadow-xs"
                  : "border-slate-200/80 bg-white hover:border-indigo-300 hover:shadow-xs"
              }`}
            >
              <div
                className={`rounded-full p-2.5 size-10 flex items-center justify-center transition-colors duration-200 ${
                  copiedLink
                    ? "bg-emerald-100 text-emerald-600 animate-bounce"
                    : "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100"
                }`}
              >
                {copiedLink ? <Check className="size-5" /> : <Link className="size-5" />}
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <span
                  className={`text-sm font-bold transition-colors duration-200 ${
                    copiedLink ? "text-emerald-700" : "text-slate-800"
                  }`}
                >
                  {copiedLink ? "Link Copied!" : "Copy Page Link"}
                </span>
                <span
                  className={`text-xs mt-0.5 transition-colors duration-200 ${
                    copiedLink ? "text-emerald-600" : "text-slate-500"
                  }`}
                >
                  {copiedLink
                    ? "Website address is in your clipboard"
                    : "Copy direct website address to share via text or chat"}
                </span>
              </div>
            </div>

            {/* Tile 2: Copy Video for Email */}
            <div
              onClick={handleCopyForEmail}
              className={`group flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 active:scale-[0.98] cursor-pointer select-none ${
                copiedForEmail
                  ? "border-emerald-500 bg-emerald-50/10 shadow-xs"
                  : "border-slate-200/80 bg-white hover:border-purple-300 hover:shadow-xs"
              }`}
            >
              <div
                className={`rounded-full p-2.5 size-10 flex items-center justify-center transition-colors duration-200 ${
                  copiedForEmail
                    ? "bg-emerald-100 text-emerald-600 animate-bounce"
                    : "bg-purple-50 text-purple-600 group-hover:bg-purple-100"
                }`}
              >
                {copiedForEmail ? <Check className="size-5" /> : <Mail className="size-5" />}
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <span
                  className={`text-sm font-bold transition-colors duration-200 ${
                    copiedForEmail ? "text-emerald-700" : "text-slate-800"
                  }`}
                >
                  {copiedForEmail ? "Video Copied!" : "Copy Video for Email"}
                </span>
                <span
                  className={`text-xs mt-0.5 transition-colors duration-200 ${
                    copiedForEmail ? "text-emerald-600" : "text-slate-500"
                  }`}
                >
                  {copiedForEmail
                    ? "Ready to paste inside Outlook, Gmail, etc."
                    : "Copies a clickable video image with a play button to paste inside your email"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={() => (onDone ? onDone() : onOpenChange(false))} className="w-full">
          Done
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
